import { memo } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Camera, MapPin, ShieldCheck } from "lucide-react";
import type { ProductCard as ProductCardData } from "@/api/types";
import { Badge, PriceTag, SmartImage, VerifiedBadge } from "@/components/ui";
import { cn } from "@/lib/cn";
import { QuickAdd } from "./QuickAdd";
import { WishlistHeart } from "./WishlistHeart";
import { shortAgo } from "@/lib/dates";

const CONDITION = { NEW: "Brand new", USED: "Used", REFURBISHED: "Refurbished" } as const;

export interface ProductCardProps {
  product: ProductCardData;
  layout?: "grid" | "list";
  className?: string;
  priority?: boolean;
}

/**
 * Listing card (grid or list): 1:1 photo, up to two badges, price, two-line title, place and age.
 * The whole card is one link; on desktop hover only lifts the shadow.
 * <ProductCard product={p} layout="list" />
 */
export const ProductCard = memo(function ProductCard({ product, layout = "grid", className, priority }: ProductCardProps) {
  const location = [product.lga_name || product.city, product.state_name].filter(Boolean).join(", ");
  const ago = shortAgo(product.published_at);
  const list = layout === "list";
  // At most two badges, in this order: RealitySafe, Verified store, Ad (boosted).
  const badges = [
    product.escrow_enabled && (
      <Badge
        key="safe"
        tone="success"
        className="border-transparent bg-[var(--safe-tint)]"
        icon={<ShieldCheck className="size-3" aria-hidden />}
      >
        RealitySafe
      </Badge>
    ),
    product.vendor.is_verified && (
      <Badge key="ver" tone="cyan" className="border-transparent bg-surface/90" icon={<BadgeCheck className="size-3" aria-hidden />}>
        Verified
      </Badge>
    ),
    (product.is_boosted || product.is_featured) && (
      <Badge key="ad" tone="neutral" className="border-transparent bg-surface/90">
        Ad
      </Badge>
    ),
  ]
    .filter(Boolean)
    .slice(0, 2);

  return (
    <div data-card className={cn("group/card relative h-full", className)}>
      <Link
        to={`/p/${product.slug}`}
        data-flip-id={product.id}
        className={cn(
          "backlit group relative flex h-full min-h-12 overflow-hidden rounded-[12px] border border-line bg-surface shadow-card transition-shadow duration-200 hover:shadow-glow-2",
          list ? "flex-row" : "flex-col",
        )}
      >
        <div
          className={cn(
            "relative shrink-0 overflow-hidden border-b border-line bg-surface2",
            list ? "w-32 border-b-0 border-r sm:w-48" : "aspect-square w-full",
          )}
        >
          <SmartImage
            src={product.cover?.url}
            alt=""
            fallbackLabel={product.title}
            compact={list}
            width={480}
            maxWidth={800}
            sizes={list ? "200px" : "(min-width: 1200px) 16vw, (min-width: 900px) 25vw, (min-width: 600px) 33vw, 50vw"}
            priority={priority}
            className="size-full"
            imgClassName="object-cover"
          />
          {badges.length > 0 && <div className="absolute left-2 top-2 flex max-w-[calc(100%-3rem)] flex-wrap gap-1">{badges}</div>}
          {product.image_count > 1 && !list && (
            <span className="tabular absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full bg-scrim/60 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur">
              <Camera className="size-3" aria-hidden /> {product.image_count}
              <span className="sr-only"> photos</span>
            </span>
          )}
        </div>
        <div className={cn("flex min-w-0 flex-1 flex-col gap-1", list ? "p-3 sm:p-4" : "p-3")}>
          <PriceTag kobo={product.price} oldKobo={product.old_price} size="sm" singleLine />
          {product.is_negotiable && <span className="-mt-0.5 text-xs text-ink-muted">Negotiable</span>}
          <h3
            className={cn("line-clamp-2 min-h-[2.6em] font-sans text-sm font-normal leading-[1.3] text-ink-soft", list && "sm:text-base")}
          >
            {product.title}
          </h3>
          <p className="mt-auto flex min-w-0 items-center gap-1 whitespace-nowrap pt-0.5 text-xs text-ink-muted">
            {location && (
              <>
                <MapPin className="size-3 shrink-0" aria-hidden />
                <span className="truncate">{location}</span>
              </>
            )}
            {product.distance_km !== null && <span className="tabular shrink-0">· {product.distance_km} km</span>}
            {ago && (
              <span className="shrink-0">
                {location ? "· " : ""}
                {ago}
              </span>
            )}
          </p>
          {list && (
            <div className="flex items-center gap-2 text-xs text-ink-soft">
              {product.vendor.store_name}
              {product.vendor.is_verified && <VerifiedBadge compact />}
              <span className="text-ink-muted">· {CONDITION[product.condition]}</span>
            </div>
          )}
        </div>
      </Link>
      <WishlistHeart
        productId={product.id}
        slug={product.slug}
        title={product.title}
        size="sm"
        className={cn("absolute z-10", list ? "right-3 top-3" : "right-2 top-2")}
      />
      {(product.escrow_enabled || product.pay_on_delivery) && !list && (
        // Overlay matching the square image so the button sits on its bottom-right corner.
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 aspect-square">
          <QuickAdd
            slug={product.slug}
            title={product.title}
            cover={product.cover?.url}
            className="pointer-events-auto absolute bottom-2 right-2"
          />
        </div>
      )}
    </div>
  );
});

export function ProductCardSkeleton({ layout = "grid" }: { layout?: "grid" | "list" }) {
  return (
    <div className={cn("overflow-hidden rounded-[12px] border border-line bg-surface", layout === "list" ? "flex" : "")} aria-hidden>
      <div className={cn("relative overflow-hidden bg-fg/[0.04]", layout === "list" ? "h-32 w-32 sm:w-48" : "aspect-square")}>
        <span className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-fg/[0.06] to-transparent" />
      </div>
      <div className="flex flex-1 flex-col gap-2 p-3.5">
        <span className="h-3.5 w-11/12 rounded bg-fg/[0.06]" />
        <span className="h-3.5 w-2/3 rounded bg-fg/[0.06]" />
        <span className="mt-1 h-5 w-1/2 rounded bg-fg/[0.08]" />
      </div>
    </div>
  );
}
